import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { z } from 'zod';
import { type GroupSnapshot, groupMemberSchema } from './contracts';

/** Where a group's transcript message says who said it, and to whom. */
export const GROUP_MESSAGE_METADATA_KEY = 'crowdex/group';

const groupMessageMetadataSchema = z.object({
  authorId: z.string(),
  authorName: z.string(),
  recipientIds: z.array(z.string()),
  notifiedParticipantIds: z.array(z.string()).optional(),
  replyTo: z.string().optional(),
  participants: z.array(groupMemberSchema),
});
export type GroupMessageMetadata = z.infer<typeof groupMessageMetadataSchema>;

/** Who said a transcript message of a group; undefined for any other message. */
export function groupMessageMetadata(
  message: Pick<CrowdexUIMessage, 'metadata'>,
): GroupMessageMetadata | undefined {
  const metadata = message.metadata as Record<string, unknown> | undefined;
  return groupMessageMetadataSchema.safeParse(metadata?.[GROUP_MESSAGE_METADATA_KEY]).data;
}

/** A room's messages as its thread's transcript, keeping what earlier copies had. */
export function groupUIMessages(
  snapshot: GroupSnapshot,
  previous: readonly CrowdexUIMessage[] = [],
): CrowdexUIMessage[] {
  const existing = new Map(previous.map((message) => [message.id, message]));
  return snapshot.messages.map((message) => {
    const old = existing.get(message.id);
    return {
      id: message.id,
      role: message.authorId === 'user' ? 'user' : 'assistant',
      parts: old?.parts ?? [{ type: 'text', text: message.text }],
      metadata: {
        ...old?.metadata,
        [GROUP_MESSAGE_METADATA_KEY]: {
          authorId: message.authorId,
          authorName: message.authorName,
          recipientIds: message.recipientIds,
          ...(message.notifiedParticipantIds === undefined
            ? {}
            : { notifiedParticipantIds: message.notifiedParticipantIds }),
          ...(message.replyTo === undefined ? {} : { replyTo: message.replyTo }),
          participants: snapshot.participants,
        },
      },
    };
  });
}
